import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Send, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';

const Contacto = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Por favor, completa todos los campos');
      return;
    }
    if (!accepted) {
      toast.error('Debes aceptar la política de privacidad para continuar');
      return;
    }

    setLoading(true);
    try {
      await api.post('/contact', formData);
      toast.success('¡Mensaje enviado! Te responderé pronto.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setAccepted(false);
    } catch (error) {
      toast.error('Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#c9a227]" />
              <span className="text-sm text-[#c9a227] font-medium">Escuela de Consciencia Akáshica</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-6 leading-tight"
              data-testid="contacto-title"
            >
              Contacto
            </h1>
            <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed max-w-2xl mx-auto">
              Si tienes dudas, sugerencias o simplemente quieres escribirme, 
              aquí estoy para escucharte.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info */}
            <div className="lg:col-span-2">
              <div className="w-12 h-[2px] bg-[#c9a227] mb-6" />
              <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-6">
                Escríbeme
              </h2>
              <p className="text-[hsl(var(--text-secondary))] mb-8 leading-relaxed">
                Respondo personalmente a todos los mensajes. Puede llevarme unos días 
                dependiendo del volumen, pero siempre llegaré a ti.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-[hsl(var(--text-tertiary))]">Email</p>
                    <p className="text-[hsl(var(--text-primary))]">contacto@akashica.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--sage))]/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-[hsl(var(--text-tertiary))]">Respuesta</p>
                    <p className="text-[hsl(var(--text-primary))]">2-3 días laborables</p>
                  </div>
                </div>
                
                <a 
                  href="https://wa.me/34654827313"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                  data-testid="contact-whatsapp"
                >
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                    <MessageCircle className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-[hsl(var(--text-tertiary))]">WhatsApp</p>
                    <p className="text-[hsl(var(--text-primary))] group-hover:text-green-600 transition-colors">Escríbeme directo</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="bg-[#faf8f5] rounded-3xl p-8 space-y-6 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-soft"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-soft"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Asunto</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="¿Sobre qué quieres escribirme?"
                    value={formData.subject}
                    onChange={handleChange}
                    className="input-soft"
                    data-testid="contact-subject"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Mensaje</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tu mensaje..."
                    value={formData.message}
                    onChange={handleChange}
                    className="input-soft min-h-[150px] resize-none"
                    data-testid="contact-message"
                  />
                </div>

                {/* RGPD first-layer info + consent */}
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="contact-consent"
                    checked={accepted}
                    onCheckedChange={setAccepted}
                    className="mt-1 border-[#c9a227]"
                    data-testid="contact-consent"
                  />
                  <label htmlFor="contact-consent" className="text-xs text-[hsl(var(--text-tertiary))] leading-relaxed cursor-pointer">
                    He leído y acepto la{' '}
                    <Link to="/privacidad" target="_blank" className="text-[#c9a227] hover:underline">Política de privacidad</Link>.
                    Tus datos serán tratados por el titular del sitio con la única finalidad de responder a tu consulta, con base en tu consentimiento. No se cederán a terceros salvo obligación legal. Puedes ejercer tus derechos escribiendo a hola@herminiovazquez.com.
                  </label>
                </div>

                <Button 
                  type="submit" 
                  className="bg-[#c9a227] hover:bg-[#b89220] text-white w-full py-6 rounded-full font-medium shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                  disabled={loading || !accepted}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    'Enviando...'
                  ) : (
                    <>
                      Enviar mensaje
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 sm:py-20 bg-[#faf8f5]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-white rounded-3xl p-10 lg:p-12 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-6 h-6 text-[#c9a227]" />
            </div>
            <blockquote className="font-heading text-xl lg:text-2xl text-[hsl(var(--text-primary))] leading-relaxed mb-6">
              "No tienes que convertirte en nadie distinto. Solo volver a casa dentro de ti."
            </blockquote>
            <p className="text-[hsl(var(--sage))] font-medium">— Herminio</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
